import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { api, setAuthTokens, clearAuthTokens } from '@/lib/api'

// Storage keys
const AUTH_STORAGE_KEY = 'kcs-auth-storage'
const REMEMBER_ME_KEY = 'kcs-remember-me'

interface User {
  id: number
  email: string
  username: string
  role: string
  organization: string | null
}

interface Tokens {
  access: string
  refresh: string
}

interface PersistedAuthState {
  tokens: Tokens | null
  isAuthenticated: boolean
  user: User | null
  loginTimestamp: number | null
  rememberMe: boolean
}

interface AuthState extends PersistedAuthState {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => Promise<void>
  refreshAccessToken: () => Promise<boolean>
  fetchCurrentUser: () => Promise<void>
  setRememberMe: (value: boolean) => void
}

interface LoginResponse {
  access: string
  refresh: string
  user: User
}

interface RefreshResponse {
  access: string
  refresh?: string
}

// Get stored rememberMe preference (always from localStorage)
const getStoredRememberMe = (): boolean => {
  try {
    const stored = localStorage.getItem(REMEMBER_ME_KEY)
    return stored === 'true'
  } catch {
    return false
  }
}

// Custom storage that switches based on rememberMe preference
const createDynamicStorage = () => {
  const getStorage = (): Storage => {
    const rememberMe = getStoredRememberMe()
    return rememberMe ? localStorage : sessionStorage
  }

  return {
    getItem: (name: string): string | null => {
      const storage = getStorage()
      const value = storage.getItem(name)
      if (!value) {
        // Fallback: check both storages for existing session
        const localValue = localStorage.getItem(name)
        const sessionValue = sessionStorage.getItem(name)
        return localValue || sessionValue
      }
      return value
    },
    setItem: (name: string, value: string): void => {
      const storage = getStorage()
      // Clear from both storages first to prevent duplicates
      localStorage.removeItem(name)
      sessionStorage.removeItem(name)
      // Save to the appropriate storage
      storage.setItem(name, value)
    },
    removeItem: (name: string): void => {
      // Clear from both storages
      localStorage.removeItem(name)
      sessionStorage.removeItem(name)
    },
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      tokens: null,
      loginTimestamp: null,
      rememberMe: getStoredRememberMe(),

      login: async (email: string, password: string, rememberMe: boolean = false) => {
        // Save rememberMe preference BEFORE making the API call
        // This ensures storage is ready when we save auth state
        localStorage.setItem(REMEMBER_ME_KEY, String(rememberMe))

        const data = await api.post<LoginResponse>('/auth/login/', { email, password })
        setAuthTokens(data.access, data.refresh)
        set({
          isAuthenticated: true,
          tokens: { access: data.access, refresh: data.refresh },
          user: data.user,
          loginTimestamp: Date.now(),
          rememberMe,
        })
      },

      logout: async () => {
        const { tokens, rememberMe } = get()
        if (tokens?.refresh) {
          try {
            await api.post('/auth/logout/', { refresh: tokens.refresh })
          } catch {
            // Ignore logout errors, clear local state anyway
          }
        }
        clearAuthTokens()
        // Keep rememberMe preference after logout so checkbox stays checked
        set({ isAuthenticated: false, user: null, tokens: null, loginTimestamp: null, rememberMe })
      },

      refreshAccessToken: async () => {
        const { tokens } = get()
        if (!tokens?.refresh) return false

        try {
          const data = await api.post<RefreshResponse>('/auth/refresh/', { refresh: tokens.refresh })
          const newRefresh = data.refresh || tokens.refresh
          setAuthTokens(data.access, newRefresh)
          set({
            tokens: { access: data.access, refresh: newRefresh },
          })
          return true
        } catch {
          clearAuthTokens()
          set({ isAuthenticated: false, user: null, tokens: null })
          return false
        }
      },

      fetchCurrentUser: async () => {
        const { tokens } = get()
        if (!tokens?.access) return

        try {
          const user = await api.get<User>('/auth/me/')
          set({ user, isAuthenticated: true })
        } catch (err) {
          const status = (err as { status: number }).status
          if (status === 401) {
            // Try refreshing token
            const refreshed = await get().refreshAccessToken()
            if (refreshed) {
              await get().fetchCurrentUser()
            }
          } else if (status === 404) {
            // User likely deleted/recreated - logout to force re-login
            get().logout()
          }
        }
      },

      setRememberMe: (value: boolean) => {
        localStorage.setItem(REMEMBER_ME_KEY, String(value))
        set({ rememberMe: value })
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => createDynamicStorage()),
      partialize: (state): PersistedAuthState => ({
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        loginTimestamp: state.loginTimestamp,
        rememberMe: state.rememberMe,
      }),
    }
  )
)
